import type { EntitlementResponse, PaidPlanId, PlanId, SubscriptionInfo } from '@repo/shared/plans';
import { readJson, writeJson } from './storage.ts';

const KEY = 'app.entitlement.v2';
const LEGACY_KEY = 'app.entitlement.v1';
/** Stripe-Freischaltungen gelten 24 Stunden – ab 12 Stunden Restlaufzeit wird erneuert. */
const RENEW_BEFORE_MS = 12 * 3_600_000;

/** Freischaltung auf diesem Gerät. Geprüft wird das Token ausschließlich vom Server. */
export interface Entitlement {
  token: string;
  plan: PlanId;
  expiresAt: number | null;
  /** null bei Dev-Freischaltungen ohne Stripe */
  subscription: SubscriptionInfo | null;
}

export function loadEntitlement(): Entitlement | null {
  const stored = readJson<Partial<Entitlement>>('local', KEY);
  if (stored && typeof stored.token === 'string') {
    return {
      token: stored.token,
      plan: stored.plan ?? 'free',
      expiresAt: stored.expiresAt ?? null,
      subscription: stored.subscription ?? null,
    };
  }
  const legacy = readJson<unknown>('local', LEGACY_KEY);
  return typeof legacy === 'string'
    ? { token: legacy, plan: 'free', expiresAt: null, subscription: null }
    : null;
}

/** Speichert die Antwort des Servers. Ohne Token wird die Freischaltung entfernt. */
export function saveEntitlement(response: EntitlementResponse | null): Entitlement | null {
  writeJson('local', LEGACY_KEY, null);
  if (!response?.token) {
    writeJson('local', KEY, null);
    return null;
  }
  const entitlement: Entitlement = {
    token: response.token,
    plan: response.plan,
    expiresAt: response.expiresAt,
    subscription: response.subscription,
  };
  writeJson('local', KEY, entitlement);
  return entitlement;
}

const PENDING_CHECKOUT_KEY = 'app.checkout.pending.v1';
/** Stripe-Bezahlseiten verfallen nach 24 Stunden – danach gibt es nichts mehr abzuholen. */
const PENDING_CHECKOUT_MAX_AGE_MS = 24 * 3_600_000;

interface PendingCheckout {
  sessionId: string;
  startedAt: number;
}

/**
 * Begonnener Kauf. Als App auf dem iPhone-Home-Bildschirm landet die Rückkehr von Stripe nicht immer
 * in der App – dann holt die App den Kauf beim nächsten Öffnen selbst ab.
 */
export function pendingCheckout(now = Date.now()): string | null {
  const stored = readJson<Partial<PendingCheckout>>('local', PENDING_CHECKOUT_KEY);
  if (typeof stored?.sessionId !== 'string' || typeof stored.startedAt !== 'number') return null;
  if (now - stored.startedAt > PENDING_CHECKOUT_MAX_AGE_MS) {
    forgetCheckout();
    return null;
  }
  return stored.sessionId;
}

export function forgetCheckout(): void {
  writeJson('local', PENDING_CHECKOUT_KEY, null);
}

/** Stripe-Freischaltungen müssen regelmäßig beim Server erneuert werden. */
export function needsRenewal(entitlement: Entitlement, now = Date.now()): boolean {
  return (
    entitlement.subscription !== null &&
    (entitlement.expiresAt === null || entitlement.expiresAt - now < RENEW_BEFORE_MS)
  );
}

export class BillingRequestError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'BillingRequestError';
    this.code = code;
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new BillingRequestError('network', 'Keine Verbindung zum Server.');
  }
  const json = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!response.ok) {
    throw new BillingRequestError(
      json.error ?? 'request_failed',
      json.message ?? `Das hat nicht geklappt (HTTP ${response.status}).`,
    );
  }
  return json as T;
}

/** Abo-Endpunkte des Servers (Stripe). */
export const billingApi = {
  /** Liefert die Adresse der Stripe-Bezahlseite und merkt sich den begonnenen Kauf. */
  checkout: async (plan: PaidPlanId) => {
    const result = await post<{ url: string; sessionId: string }>('/api/billing/checkout', {
      plan,
    });
    const pending: PendingCheckout = { sessionId: result.sessionId, startedAt: Date.now() };
    writeJson('local', PENDING_CHECKOUT_KEY, pending);
    return result;
  },
  claim: (sessionId: string) => post<EntitlementResponse>('/api/billing/claim', { sessionId }),
  renew: (token: string) => post<EntitlementResponse>('/api/billing/refresh', { token }),
  setCancel: (token: string, cancel: boolean) =>
    post<EntitlementResponse>('/api/billing/cancel', { token, cancel }),
  switchPlan: (token: string, plan: PaidPlanId) =>
    post<EntitlementResponse>('/api/billing/switch', { token, plan }),
};

/** Nur Entwicklung: Tarif ohne Zahlung simulieren (Server muss DEV_PLANS erlauben). */
export async function devActivatePlan(plan: PlanId): Promise<EntitlementResponse> {
  const result = await post<Omit<EntitlementResponse, 'subscription'>>('/api/dev/entitlement', {
    plan,
  });
  return { ...result, subscription: null };
}
